import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Package, GraduationCap, ExternalLink, ShoppingBag, ArrowLeft, CreditCard } from "lucide-react";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Brand from "../components/Brand";

export default function MemberStorefront() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [paying, setPaying] = useState(null); // id of item currently being paid

  useEffect(() => {
    api.get(`/u/${username}/storefront`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.status === 404 ? "Seller not found" : "Couldn't load this storefront"))
      .finally(() => setLoading(false));

    // Detect ?paid=... return from Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get("paid")) {
      toast.success("★ Payment successful! Check your email — the seller will follow up.");
      const url = new URL(window.location.href);
      url.searchParams.delete("paid");
      url.searchParams.delete("kind");
      window.history.replaceState({}, "", url.toString());
    }
    if (params.get("cancelled")) {
      toast("Checkout cancelled — no charge made.");
      const url = new URL(window.location.href);
      url.searchParams.delete("cancelled");
      window.history.replaceState({}, "", url.toString());
    }
  }, [username]);

  const buyProduct = async (productId) => {
    setPaying(productId);
    try {
      const r = await api.post(`/products/${productId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      setPaying(null);
      toast.error(err.response?.data?.detail || "Couldn't start checkout — the seller may not have payments set up yet.");
    }
  };

  const buyCourse = async (courseId) => {
    setPaying(courseId);
    try {
      const r = await api.post(`/seller-courses/${courseId}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      setPaying(null);
      toast.error(err.response?.data?.detail || "Couldn't start checkout — the seller may not have payments set up yet.");
    }
  };

  if (loading) return <Shell><div className="text-center text-foreground/60 py-20">Loading store…</div></Shell>;
  if (err) return <Shell><div className="text-center py-20"><div className="font-serif-display text-3xl text-red-wine uppercase">{err}</div><Link to="/" className="text-red-wine font-bold underline mt-4 inline-block">← Back home</Link></div></Shell>;

  const { seller, products, courses, counts } = data;
  const isEmpty = (counts.products + counts.courses) === 0;

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <Link to={`/u/${username}`} className="text-xs uppercase tracking-wider-cap font-bold text-foreground/55 hover:text-red-wine inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="w-3.5 h-3.5"/> Back to {seller.full_name || seller.username}'s profile</Link>

        {/* Seller card */}
        <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#2A2521] text-white p-6 md:p-8 shadow-2xl border-2 border-[#F39A1F]/40 mb-8" data-testid="storefront-seller">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-5">
            {seller.profile_photo ? (
              <img src={seller.profile_photo} alt="" className="w-20 h-20 rounded-full object-cover border-4 border-[#F39A1F]"/>
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#F39A1F]/20 flex items-center justify-center"><ShoppingBag className="w-8 h-8 text-[#F39A1F]"/></div>
            )}
            <div className="text-center md:text-left flex-1">
              <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ <Brand/> STOREFRONT</div>
              <h1 className="font-serif-display text-3xl md:text-4xl uppercase text-white leading-tight">{seller.full_name || seller.username}</h1>
              {seller.headline && <div className="text-white/80 italic mt-1">{seller.headline}</div>}
              {seller.bio && <p className="text-white/70 text-sm mt-2 max-w-xl">{seller.bio}</p>}
              {seller.location && <div className="text-[10px] tracking-wider-cap text-white/55 mt-2">★ {seller.location}</div>}
            </div>
          </div>
        </div>

        {isEmpty ? (
          <div className="text-center py-20 rounded-2xl bg-white border-2 border-dashed border-foreground/20" data-testid="storefront-empty">
            <ShoppingBag className="w-12 h-12 text-foreground/30 mx-auto mb-2"/>
            <div className="font-serif-display text-2xl text-foreground/55 uppercase">Nothing for sale yet.</div>
            <p className="text-sm text-foreground/55 mt-1">Check back soon — they're building.</p>
          </div>
        ) : (
          <>
            {courses.length > 0 && (
              <section className="mb-12" data-testid="storefront-courses">
                <div className="flex items-baseline gap-3 mb-5">
                  <GraduationCap className="w-6 h-6 text-gold-deep"/>
                  <h2 className="font-serif-display text-3xl uppercase text-red-wine">Courses</h2>
                  <div className="text-[10px] tracking-wider-cap font-bold text-foreground/55 ml-auto">{courses.length} {courses.length === 1 ? "course" : "courses"}</div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {courses.map(c => <PublicCourseCard key={c.id} course={c} onBuy={buyCourse} paying={paying === c.id}/>)}
                </div>
              </section>
            )}
            {products.length > 0 && (
              <section data-testid="storefront-products">
                <div className="flex items-baseline gap-3 mb-5">
                  <Package className="w-6 h-6 text-gold-deep"/>
                  <h2 className="font-serif-display text-3xl uppercase text-red-wine">Products</h2>
                  <div className="text-[10px] tracking-wider-cap font-bold text-foreground/55 ml-auto">{products.length} {products.length === 1 ? "product" : "products"}</div>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map(p => <PublicProductCard key={p.id} product={p} onBuy={buyProduct} paying={paying === p.id}/>)}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </Shell>
  );
}

function PublicProductCard({ product, onBuy, paying }) {
  return (
    <div className="bg-white rounded-xl border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-xl transition flex flex-col overflow-hidden" data-testid={`pub-product-${product.id}`}>
      <div className="aspect-[16/10] bg-foreground/5 relative overflow-hidden">
        {product.image_url ? <img src={product.image_url} alt="" className="w-full h-full object-contain p-3 bg-white" loading="lazy"/> : <div className="w-full h-full flex items-center justify-center"><Package className="w-12 h-12 text-foreground/20"/></div>}
        <div className="absolute top-2 right-2 bg-[#F39A1F] text-[#0F172A] text-xs font-bold px-2.5 py-0.5 rounded-full">${product.price_usd.toFixed(2)}</div>
        <div className="absolute bottom-2 left-2 text-[9px] font-bold uppercase tracking-wider-cap px-2 py-0.5 rounded-full bg-onyx/70 text-white backdrop-blur-sm">{product.kind === "digital" ? "DIGITAL" : "PHYSICAL"}</div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-red-wine text-base leading-tight mb-1 line-clamp-2">{product.title}</div>
        <div className="text-xs text-foreground/65 line-clamp-3 mb-3 flex-1">{product.description}</div>
        <BuyButton item={product} onBuy={onBuy} paying={paying} testid={`buy-product-${product.id}`} icon={CreditCard}/>
      </div>
    </div>
  );
}

function PublicCourseCard({ course, onBuy, paying }) {
  const isFree = course.price_usd <= 0;
  return (
    <div className="bg-white rounded-xl border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-xl transition flex flex-col overflow-hidden" data-testid={`pub-course-${course.id}`}>
      <div className="aspect-[16/9] bg-foreground/5 relative overflow-hidden">
        {course.thumbnail ? <img src={course.thumbnail} alt="" className="w-full h-full object-contain p-3 bg-white" loading="lazy"/> : <div className="w-full h-full flex items-center justify-center"><GraduationCap className="w-12 h-12 text-foreground/20"/></div>}
        <div className="absolute top-2 right-2 bg-[#F39A1F] text-[#0F172A] text-xs font-bold px-2.5 py-0.5 rounded-full">{isFree ? "FREE" : `$${course.price_usd.toFixed(2)}`}</div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-red-wine text-base leading-tight mb-1 line-clamp-2">{course.title}</div>
        <div className="text-xs text-foreground/65 line-clamp-3 mb-2 flex-1">{course.description}</div>
        <div className="text-[10px] tracking-wider-cap font-bold text-foreground/50 mb-3">{course.lesson_count || 0} {(course.lesson_count || 0) === 1 ? "LESSON" : "LESSONS"}</div>
        {isFree ? (
          <div className="bg-[#22693C] text-white px-3 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs text-center">★ Free Preview</div>
        ) : (
          <BuyButton item={course} onBuy={onBuy} paying={paying} testid={`buy-course-${course.id}`} icon={GraduationCap} verb="Enroll"/>
        )}
      </div>
    </div>
  );
}

/**
 * Smart Buy button:
 *   1) If seller has Stripe Connect onboarded → real "Buy Now" → opens Stripe Checkout
 *   2) Else if seller pasted a `purchase_link` → external "Buy via PayPal/etc." link
 *   3) Else → "Coming Soon · Seller setting up payments"
 *
 * NOTE: Stripe Connect is currently paused (Mike hasn't completed Stripe Go-Live KYC),
 * so we PREFER the seller's external payment link until that's wired.
 */
function BuyButton({ item, onBuy, paying, testid, icon: Icon, verb = "Buy" }) {
  const hasFallbackLink = !!(item.purchase_link && item.purchase_link.trim());
  if (paying) {
    return <div className="bg-[#2E9F3A]/70 text-white px-3 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs text-center inline-flex items-center justify-center gap-1.5"><Icon className="w-3.5 h-3.5"/> Redirecting…</div>;
  }
  if (hasFallbackLink) {
    return (
      <a href={item.purchase_link} target="_blank" rel="noopener noreferrer" data-testid={testid} className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-3 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs text-center inline-flex items-center justify-center gap-1.5 shadow"><Icon className="w-3.5 h-3.5"/> {verb} · ${item.price_usd.toFixed(2)} <ExternalLink className="w-3 h-3"/></a>
    );
  }
  return <div className="bg-foreground/10 text-foreground/50 px-3 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs text-center" data-testid={`${testid}-coming-soon`}>Checkout Setup By Seller</div>;
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <SiteNav />
      <main>{children}</main>
      <SiteFooter />
    </div>
  );
}
